import { Component, computed, inject, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { TableModule } from "@openng/optimus-ui/table";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { GateIdentifierPipe } from "../../../../shared/pipes/gateIdentifier/gate-identifier.pipe";
import { DecimalPipe, NgClass } from "@angular/common";
import { RepairStatusPipe } from "../../../../shared/pipes/repairStatus/repair-status.pipe";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { GateFaultService } from "../../../../services/api/gate-fault/gate-fault.service";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { REPAIR_STATUS } from "../../../../enums/repairStatus.enum";
import { MessageService } from "@openng/optimus-ui/api";
import { DynamicDialogRef, DialogService } from "@openng/optimus-ui/dynamicdialog";
import { FixLinkComponent } from "../../../../shared/components/fix-link/fix-link.component";
import { PAGINATOR_CONSTANTS } from "../../../../constants/paginator";
import { ActivatedRoute } from "@angular/router";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { Column } from "../../../../models/ui/tableColumn";
import { Reference } from "../../../../models/shared/refs";
import { pathToAssetType } from "../../../../shared/utils/assetType.utils";
import { first } from "rxjs";
import { TableStatusService } from "./facade/table-status.service";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-status-table",
  standalone: true,
  imports: [
    SvgIconComponent,
    TooltipModule,
    TableModule,
    DecimalPipe,
    RepairStatusPipe,
    NgClass,
    TranslateModule,
    TimeoutComponent,
    ProgressSpinnerModule,
    HasPermissionDirective,
  ],
  templateUrl: "./status-table.component.html",
  styleUrl: "./status-table.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TableStatusService],
})
export class StatusTableComponent implements OnDestroy {
  public COLUMNS: Column[] = [
    { field: "severity", header: "status.table.status" },
    { field: "gateSystem", header: "status.table.gate_system" },
    { field: "gateManufacturer", header: "status.table.gate_manufacturer" },
    {
      field: "controlManufacturer",
      header: "status.table.control_manufacturer",
    },
    { field: "controlModel", header: "status.table.control_model" },
    {
      field: "Display_Sensor_Temp",
      header: "status.table.temp_inside",
      unit: "units.celcius",
    },
    {
      field: "Display_Sensor_Humi",
      header: "status.table.humi_inside",
      unit: "units.percentage",
    },
    {
      field: "gate_fully_open_movement_time_avg",
      header: "status.table.gate_open_duration",
      unit: "units.seconds_abbr",
    },
    {
      field: "gate_fully_close_movement_time_avg",
      header: "status.table.gate_close_duration",
      unit: "units.seconds_abbr",
    },
    { field: "cycle", header: "status.table.load_changes" },
    { field: "gate_full_cycles", header: "status.table.load_cycles" },
    { field: "repairStatus", header: "status.table.repair_status" },
    {
      field: "dirtyUseRatio",
      header: "status.table.dirty_use_ratio",
      unit: "units.percentage",
    },
  ];

  public paginatorOptions = PAGINATOR_CONSTANTS;

  public permissions = inject(PermissionService);
  public gateIdentifierPipe = inject(GateIdentifierPipe);
  private gateFaultService = inject(GateFaultService);
  private translate = inject(TranslateService);
  public statusFacade = inject(TableStatusService);

  public REPAIR_STATUS = REPAIR_STATUS;

  private ref: DynamicDialogRef | null = null;
  private dialogService = inject(DialogService);
  private message = inject(MessageService);

  public selectedCols!: Column[];

  public loading = computed(() => this.statusFacade.table() === null);
  public timeOut = createTimeoutSignal(this.loading);

  private route = inject(ActivatedRoute);

  private sourceId!: string;
  private sourceType!: string;

  constructor() {
    this.route.url.pipe(takeUntilDestroyed()).subscribe({
      next: (url) => {
        const sourceId = url[1].path;
        const sourceType = pathToAssetType(url[0].path);

        if (sourceId && sourceType) {
          this.sourceId = sourceId;
          this.sourceType = sourceType;
          this.statusFacade.selectAssetData(sourceId, sourceType);
        }
      },
    });
    this.selectedCols = this.COLUMNS;
  }

  public ngOnDestroy() {
    this.statusFacade.destroy();
  }

  /**
   * Returns true if the value is a number.
   * @param value The value to check if it is a number.
   * @returns True if the value is a number, false otherwise.
   */
  public isNumber(value: string) {
    return typeof value === "number";
  }

  /**
   * Opens a modal for sending a repair request for the given asset.
   * @param asset The asset to open the modal for.
   */
  public openRepairModal(asset: Reference, title: string) {
    this.gateFaultService
      .getGateFaults(asset.id, ASSET_TYPES.GATE_SYSTEM, true)
      .pipe(first())
      .subscribe({
        next: (res) => {
          const gateFault = res.find((fault) => {
            return (
              fault.active &&
              (fault.repairStatus === null ||
                fault.repairStatus === REPAIR_STATUS.None ||
                fault.repairStatus === REPAIR_STATUS.Archived)
            );
          });
          if (gateFault) {
            this.ref = this.dialogService.open(FixLinkComponent, {
              header: this.translate.instant(`gate_faults.repair_modal.title.${title}`),
              modal: true,
              width: "50vw",
              contentStyle: { overflow: "auto" },
              breakpoints: {
                "960px": "75vw",
                "640px": "90vw",
              },
              data: {
                fixLink: gateFault.fixLink,
                repairStatus: gateFault.repairStatus,
                gateFaultId: gateFault.id,
              },
              closable: true,
            });

            this.ref?.onClose.subscribe((res) => {
              if (res) {
                switch (res) {
                  case "request_success":
                    this.message.add({
                      key: "global",
                      severity: "success",
                      summary: this.translate.instant("common.success"),
                      detail: this.translate.instant("gate_faults.repair_modal.request_success"),
                      life: 5000,
                    });
                    this.statusFacade.selectAssetData(this.sourceId, this.sourceType);
                    break;
                }
              }
            });
          }
        },
      });
  }
}
